import type { OmnichannelNewMessageEvent } from '@/types/omnichannel';

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function pickClassificationString(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed ? trimmed : undefined;
}

/** HOT/WARM/COLD may sit on the event root or nested under `message`. */
export function extractOmnichannelClassification(
  root: Record<string, unknown>,
  message?: Record<string, unknown>
): string | undefined {
  const candidates = [
    root.classification,
    root.leadClassification,
    root.leadStatus,
    root.lead_status,
    message?.classification,
    message?.leadClassification,
    message?.leadStatus,
    message?.lead_status,
  ];
  for (const c of candidates) {
    const s = pickClassificationString(c);
    if (s) return s;
  }
  return undefined;
}

function normalizeChannelMessage(msg: Record<string, unknown>) {
  const id =
    typeof msg.id === 'string'
      ? msg.id
      : typeof msg.id === 'number'
        ? String(msg.id)
        : '';
  const content =
    typeof msg.content === 'string'
      ? msg.content
      : typeof msg.message === 'string'
        ? msg.message
        : null;
  if (!id) return null;
  return {
    id,
    content,
    messageType:
      typeof msg.messageType === 'string' ? msg.messageType : undefined,
    senderId:
      typeof msg.senderId === 'string'
        ? msg.senderId
        : typeof msg.senderId === 'number'
          ? String(msg.senderId)
          : undefined,
    createdAt: typeof msg.createdAt === 'string' ? msg.createdAt : undefined,
  };
}

export type ParsedOmnichannelRealtimePayload =
  | {
      kind: 'channel_message';
      event: OmnichannelNewMessageEvent;
      classification?: string;
    }
  | {
      kind: 'ai_response';
      conversationId: string;
      reply: string;
      classification?: string;
    }
  | {
      kind: 'lead_classification';
      conversationId: string;
      classification: string;
    };

/**
 * Normalizes Socket.IO `new_message` payloads: channel traffic, `ai_response` (reply via WebSocket), nested `data`, or stringified JSON.
 */
export function parseOmnichannelNewMessagePayload(
  raw: unknown
): ParsedOmnichannelRealtimePayload | null {
  let cur: unknown = raw;

  if (typeof cur === 'string') {
    try {
      cur = JSON.parse(cur) as unknown;
    } catch {
      return null;
    }
  }

  if (Array.isArray(cur) && cur.length > 0) {
    const first = cur[0];
    if (isRecord(first) && typeof first.value === 'string') {
      try {
        cur = JSON.parse(first.value) as unknown;
      } catch {
        return null;
      }
    }
  }

  if (isRecord(cur) && 'data' in cur) {
    const inner = cur.data;
    if (typeof inner === 'string') {
      try {
        cur = JSON.parse(inner) as unknown;
      } catch {
        cur = inner;
      }
    } else {
      cur = inner;
    }
  }

  if (!isRecord(cur)) return null;

  const conversationIdRaw = cur.conversationId;
  const convIdStr =
    typeof conversationIdRaw === 'number' ||
    typeof conversationIdRaw === 'string'
      ? String(conversationIdRaw)
      : null;

  if (!isRecord(cur.message)) return null;

  const classification = extractOmnichannelClassification(cur, cur.message);
  const messageType =
    typeof cur.message.type === 'string' ? cur.message.type : '';

  if (messageType === 'ai_response') {
    const reply =
      typeof cur.message.reply === 'string' ? cur.message.reply.trim() : '';
    if (convIdStr === null || (!reply && !classification)) return null;
    return {
      kind: 'ai_response',
      conversationId: convIdStr,
      reply,
      classification,
    };
  }

  if (
    convIdStr &&
    classification &&
    (messageType === 'classification' ||
      messageType === 'lead_classification' ||
      messageType === 'lead_status')
  ) {
    return {
      kind: 'lead_classification',
      conversationId: convIdStr,
      classification,
    };
  }

  let accountId =
    typeof cur.accountId === 'number'
      ? cur.accountId
      : typeof cur.accountId === 'string'
        ? Number(cur.accountId)
        : undefined;
  if (accountId === undefined || Number.isNaN(accountId)) {
    accountId = 0;
  }
  const channelRaw = typeof cur.channel === 'string' ? cur.channel.trim() : '';
  const channel = channelRaw ? channelRaw.toLowerCase() : undefined;
  const convId =
    typeof conversationIdRaw === 'number' ||
    typeof conversationIdRaw === 'string'
      ? conversationIdRaw
      : undefined;

  if (!channel || convId === undefined) {
    return null;
  }

  const message = normalizeChannelMessage(cur.message);
  if (!message) return null;

  return {
    kind: 'channel_message',
    event: {
      accountId,
      channel,
      conversationId: convId,
      message,
    },
    classification,
  };
}
